import { useState } from "react";
import { Activity, ChevronRight, Trash2, GitBranch } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * ClinicalReasoningTimeline — render a session's clinical_capture[] as a
 * Finding → Decision → Technique → Response → Next Step timeline.
 */
export default function ClinicalReasoningTimeline({ captures = [], sessionId, onChange, recurringPatterns = [] }) {
  if (!captures.length && !recurringPatterns.length) {
    return (
      <div className="bg-paper-50 border border-paper-200 rounded-2xl p-6 text-center" data-testid="timeline-empty">
        <Activity className="w-8 h-8 mx-auto text-ink-300 mb-2" />
        <div className="text-sm text-ink-700">No live captures yet. Tap the floating mic during treatment to dictate findings.</div>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="clinical-timeline">
      {recurringPatterns.length > 0 && (
        <RecurringPatternsBanner patterns={recurringPatterns} />
      )}
      {captures.map((c, i) => (
        <TimelineRow key={c.id || i} c={c} sessionId={sessionId} onChange={onChange} index={i + 1} />
      ))}
    </div>
  );
}

function RecurringPatternsBanner({ patterns }) {
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="recurring-patterns-banner">
      <div className="flex items-center gap-2 mb-2">
        <GitBranch className="w-4 h-4 text-amber-700" />
        <div className="label-overline text-amber-700">Recurring clinical patterns</div>
      </div>
      <div className="text-sm text-ink-700 mb-2">These restrictions have appeared in <b>{patterns[0].session_count}+ sessions</b> with this client. The AI is using them as context for today's plan.</div>
      <div className="flex flex-wrap gap-2">
        {patterns.map((p) => (
          <span key={p.key} className="text-xs px-3 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-2" data-testid={`pattern-chip-${p.key}`}>
            {p.label}
            <span className="text-[10px] bg-white/60 rounded-full px-1.5">{p.session_count}× sessions</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function TimelineRow({ c, sessionId, onChange, index }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!window.confirm("Delete this capture?")) return;
    setBusy(true);
    try {
      await api.delete(`/sessions/${sessionId}/captures/${c.id}`);
      toast.success("Capture removed");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-paper-200 rounded-2xl p-4 sm:p-5 relative group" data-testid={`timeline-row-${c.id}`}>
      <div className="absolute -left-2 top-5 hidden sm:flex w-6 h-6 rounded-full bg-sage-500 text-white text-[10px] items-center justify-center font-medium">{index}</div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="font-heading text-base font-medium text-ink-900">
          {c.side ? <span className="text-terracotta-600">{c.side[0].toUpperCase() + c.side.slice(1)} </span> : null}
          {c.body_region || "Capture"}
          {c.severity != null && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-paper-100 text-ink-700">sev {c.severity}/10</span>}
          {c.added_to_longitudinal && <span className="ml-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100">longitudinal</span>}
        </div>
        <button
          onClick={remove}
          disabled={busy}
          className="opacity-0 group-hover:opacity-100 sm:opacity-60 sm:hover:opacity-100 w-8 h-8 rounded-full hover:bg-paper-100 hover:text-destructive flex items-center justify-center text-ink-500 transition-all"
          data-testid={`timeline-delete-${c.id}`}
          aria-label="Delete capture"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 text-sm">
        <Step label="Finding" value={[c.tissue_quality, c.location_size].filter(Boolean).join(" · ")} testid={`step-finding-${c.id}`} />
        <Step label="Decision" value={c.therapist_reasoning} testid={`step-decision-${c.id}`} />
        <Step label="Technique" value={c.technique_used} testid={`step-technique-${c.id}`} />
        <Step label="Response" value={c.client_response} testid={`step-response-${c.id}`} />
        <Step label="Next step" value={c.recheck_item} testid={`step-next-${c.id}`} />
      </div>

      {c.suspected_muscles?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-paper-100 flex flex-wrap gap-1">
          {c.suspected_muscles.map((m, i) => (
            <span key={`${m}-${i}`} className="text-[10px] px-2 py-0.5 rounded-full bg-sage-50 text-sage-700 border border-sage-100">{m}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function Step({ label, value, testid }) {
  return (
    <div className="flex-1 min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-ink-500 mb-0.5">{label}</div>
      <div className="text-ink-900 break-words">{value || <span className="text-ink-300">—</span>}</div>
    </div>
  );
}
